import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { useDrawerNav } from "@/common/AppDrawer";
import { useCMWFeatureFlagMutation } from "@/api/cmw-feature-flags";

import { TableFeatureFlag, TableOrganizationFlags } from "./types";

export function useCMWFeatureFlagHandlers(refetch?: () => void) {
  const drawer = useDrawerNav();
  const history = useHistory();
  const [updateFeatureFlag] = useCMWFeatureFlagMutation();

  const handleGlobalToggle = useCallback(
    async (flagName: string, enabled: boolean) => {
      await updateFeatureFlag({
        flag: flagName,
        payload: { enabled },
      });
      // Refetch will be handled by the table query
    },
    [updateFeatureFlag]
  );

  const handleRowPress = useCallback(
    (row: TableFeatureFlag) => {
      drawer.push("cmwFeatureFlagDetails", { flagName: row.flagName });
    },
    [drawer]
  );

  const navigateToOrganization = useCallback(
    (orgId: number) => {
      history.push(`/organizations/${orgId}`);
    },
    [history]
  );

  const navigateToGroup = useCallback(
    (groupId: number) => {
      history.push(`/groups/${groupId}`);
    },
    [history]
  );

  const handleRemoveGate = useCallback(
    async (
      flagName: string,
      entityType: "organization" | "group",
      entityId: number
    ) => {
      const payload: { enabled: boolean | null } & Record<string, any> = {
        enabled: null,
      };

      if (entityType === "organization") {
        payload.organization_id = entityId;
      } else {
        payload.group_id = entityId;
      }

      await updateFeatureFlag({
        flag: flagName,
        payload,
      });

      if (refetch) {
        refetch();
      }
    },
    [updateFeatureFlag, refetch]
  );

  return {
    handleGlobalToggle,
    handleRowPress,
    navigateToOrganization,
    navigateToGroup,
    handleRemoveGate,
  };
}

export function useOrganizationFlagsHandlers() {
  const drawer = useDrawerNav();

  const handleOrgFlagsPress = useCallback(
    (row: TableOrganizationFlags) => {
      drawer.push("cmwOrgFlagsDetails", {
        organizationId: row.organizationId,
        organizationName: row.organizationName,
      });
    },
    [drawer]
  );

  const handleGroupFlagsPress = useCallback(
    (row: TableOrganizationFlags) => {
      drawer.push("cmwGroupFlagsDetails", {
        organizationId: row.organizationId,
        organizationName: row.organizationName,
      });
    },
    [drawer]
  );

  const handleUserFlagsPress = useCallback(
    (row: TableOrganizationFlags) => {
      drawer.push("cmwUserFlagsDetails", {
        organizationId: row.organizationId,
        organizationName: row.organizationName,
      });
    },
    [drawer]
  );

  return {
    handleOrgFlagsPress,
    handleGroupFlagsPress,
    handleUserFlagsPress,
  };
}
